import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, exhaustMap, from, map, of, switchMap, tap } from 'rxjs';
import { User } from '../../models/user.model';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { LanguageService } from '../../services/language-service';
import * as AuthActions from '../actions/auth.actions';
import {
  clearSessionState,
  deleteMyAccount,
  deleteMyAccountFailure,
  deleteMyAccountSuccess,
  loadUserSuccess,
} from '../actions/auth.actions';
import { getTopCars } from '../actions/car.actions';
import { getTopRealEstates } from '../actions/real-estate.actions';
import { clearUsers } from '../actions/users.action';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private apiService: ApiService,
    private toastr: ToastrService,
    private router: Router,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {}

  // 1) login effect: call API, save token, emit loginSuccess
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ email, password }) =>
        this.authService.login({ email, password }).pipe(
          map((tokens) =>
            AuthActions.loginSuccess({
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
            })
          ),
          catchError((error) => of(AuthActions.loginFailure({ error })))
        )
      )
    )
  );

  loginWithGmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithGmail),
      switchMap(({ idToken }) =>
        this.authService.loginWithGmail({ idToken }).pipe(
          map((tokens) =>
            AuthActions.loginSuccess({
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
            })
          ),
          catchError((error) => of(AuthActions.loginFailure({ error })))
        )
      )
    )
  );

  loginWithFAcebook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithFacebook),
      switchMap(({ accessToken }) =>
        this.authService.loginWithFacebook({ accessToken }).pipe(
          map((tokens) =>
            AuthActions.loginSuccess({
              accessToken: tokens.accessToken,
              refreshToken: tokens.refreshToken,
            })
          ),
          catchError((error) => of(AuthActions.loginFailure({ error })))
        )
      )
    )
  );

  storeTokens$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ accessToken, refreshToken }) => {
          this.authService.storeTokens({ accessToken, refreshToken });
        })
      ),
    { dispatch: false }
  );

  refreshSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.refreshSuccess),
        tap(({ accessToken, refreshToken }) => {
          this.authService.storeTokens({ accessToken, refreshToken });
        })
      ),
    { dispatch: false }
  );

  loginRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadUserSuccess), // login fully completed
        tap(() => {
          // Session restoration must not replace this public page with a token-free URL
          // before its component captures the fragment (or interrupt confirmation).
          if (window.location.pathname.replace(/\/$/, '') === '/saved-searches/unsubscribe') {
            this.authService.redirectUrl = null;
            return;
          }
          let redirect = this.authService.redirectUrl || '/';
          this.authService.redirectUrl = null;

          if (redirect.startsWith('/reset-password')) {
            redirect = '/';
          }

          this.router.navigateByUrl(redirect);
        })
      ),
    { dispatch: false }
  );

  loginError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        tap(({ error }) => {
          this.toastr.error(
            this.translate.instant(error?.error?.detail === 'TERMS_UNAVAILABLE' || error?.error?.detail === 'TERMS_REQUIRED' ? error.error.detail : 'LOGIN_FAIL_ERROR'),
            this.translate.instant('ERROR')
          );
        })
      ),
    { dispatch: false }
  );

  // 2) after loginSuccess, decode token -> load user
  loadUserAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess, AuthActions.refreshSuccess),
      switchMap(({ accessToken }) => {
        const decoded = this.authService.decodeToken(accessToken);

        const userId = decoded?.userId;

        if (!userId) {
          return of(AuthActions.loadUserFailure({ error: 'Invalid token' }));
        }

        return this.apiService.getUserById(userId).pipe(
          map((user) => AuthActions.loadUserSuccess({ user })),
          catchError((error) => {
            console.error('User load failed:', error);
            return of(AuthActions.loadUserFailure({ error }));
          })
        );
      })
    )
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUser),
      switchMap(({ userId }) => {
        return this.apiService.getUserById(userId).pipe(
          map((user: User) => AuthActions.loadUserSuccess({ user })),
          catchError((error) => of(AuthActions.loadUserFailure({ error })))
        );
      })
    )
  );

  // 3) initAuth effect: run on app start (dispatch initAuth) and restore state if token exists
  initAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initAuth),
      switchMap(() => {
        const accessToken = this.authService.getAccessToken();
        const refreshToken = this.authService.getRefreshToken();

        // No tokens at all
        if (!accessToken && !refreshToken) {
          return of(AuthActions.clearAuthUser());
        }

        // Access token exists and is still valid
        if (accessToken && !this.authService.isTokenExpired(accessToken)) {
          return of(
            AuthActions.loginSuccess({
              accessToken,
              refreshToken: refreshToken!,
            })
          );
        }

        // Access token expired but refresh token exists → try refresh
        if (refreshToken) {
          return this.authService.refreshToken().pipe(
            map((tokens) =>
              AuthActions.refreshSuccess({
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
              })
            ),
            catchError(() => of(AuthActions.logout()))
          );
        }

        // Fallback
        return of(AuthActions.logout());
      })
    )
  );

  readonly applyAuthenticatedUserLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserSuccess),

        switchMap(({ user }) =>
          from(this.languageService.applyUserLanguage(user.preferredLanguage))
        )
      ),
    {
      dispatch: false,
    }
  );

  // 4) logout side effect: clear storage (non-dispatching)
  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          this.router.navigateByUrl('/');
        })
      ),
    { dispatch: false }
  );

  getTopCarsAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserSuccess),
      map(() => getTopCars())
    )
  );

  getTopPropertiesAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserSuccess),
      map(() => getTopRealEstates())
    )
  );

  getTopCarsAfterLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => getTopCars())
    )
  );

  getTopPropertiesAfterLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => getTopRealEstates())
    )
  );

  clearUsersAfterLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      map(() => clearUsers())
    )
  );

  deleteMyAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteMyAccount),
      exhaustMap(() =>
        this.apiService.deleteMyAccount().pipe(
          map(() => deleteMyAccountSuccess()),
          catchError((error) => of(deleteMyAccountFailure({ error })))
        )
      )
    )
  );

  deleteMyAccountSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteMyAccountSuccess),
        tap(async () => {
          clearSessionState();

          void this.router.navigateByUrl('/home', {
            replaceUrl: true,
          });

          this.toastr.success(this.translate.instant('ACCOUNT.DELETE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  clearSessionAfterAccountDeletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteMyAccountSuccess),
      map(() => clearSessionState())
    )
  );
}
